import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LangContext";
import logoEmblem from "../assets/logo-emblem-dark.png";

/**
 * Slide-in navigation drawer opened by the hamburger button in Header.
 * Holds every route link so the top bar can stay minimal.
 */
export default function HeaderNavPanel({ open, onClose }) {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();

  // Lock body scroll while the panel is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Close on Esc
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const go = (path) => { onClose(); nav(path); };

  const primaryLinks = [
    { to: "/", label: "Home Page", testid: "home" },
    { to: "/catalog?category=watches", label: t("catalog.watches"), testid: "watches" },
    { to: "/catalog?category=vintage_watch", label: "Vintage Watches", testid: "vintage-watches" },
    { to: "/catalog?category=bags", label: t("catalog.bags"), testid: "bags" },
    { to: "/catalog?category=accessories", label: t("catalog.accessories"), testid: "accessories" },
    { to: "/auctions", label: t("nav.auctions"), testid: "auctions" },
    { to: "/atelier", label: t("nav.services"), testid: "atelier" },
    { to: "/evaluate", label: "Evaluate Your Watch", testid: "evaluate" },
    { to: "/find-my-watch", label: "Find My Watch", testid: "find-my-watch" },
    { to: "/request-watch", label: t("nav.request_watch"), testid: "request-watch" },
    { to: "/sell", label: "Sell Your Watch", testid: "sell" },
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        aria-hidden={!open}
        onClick={onClose}
        className={`fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        data-testid="header-nav-backdrop"
      />
      {/* Panel */}
      <aside
        aria-hidden={!open}
        data-testid="header-nav-panel"
        className={`fixed top-0 start-0 z-[81] h-full w-[min(92vw,380px)] glass-strong border-e border-white/10 transition-transform duration-300 ease-out ${open ? "translate-x-0" : "-translate-x-full rtl:translate-x-full"}`}
      >
        <div className="flex items-center justify-between px-6 pt-6 pb-8">
          <img src={logoEmblem} alt="Mazad Abu Dhabi" className="h-10 w-10 object-contain" />
          <button
            onClick={onClose}
            aria-label="Close menu"
            data-testid="header-nav-close"
            className="w-9 h-9 flex items-center justify-center rounded-full border border-white/15 hover:border-gold hover:text-gold transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <nav className="px-6 flex flex-col text-base" data-testid="header-nav-links">
          {primaryLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={onClose}
              data-testid={`drawer-link-${l.testid}`}
              className="py-2.5 border-b border-white/5 hover:text-gold transition-colors font-serif tracking-wide"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-0 start-0 end-0 px-6 pb-8 pt-6 border-t border-white/10">
          {user ? (
            <button
              onClick={() => { onClose(); logout(); nav("/"); }}
              data-testid="drawer-signout"
              className="w-full border border-white/20 hover:border-red-400 hover:text-red-300 py-2.5 uppercase text-xs tracking-widest"
            >
              Sign out
            </button>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => go("/login")} data-testid="drawer-signin" className="border border-white/20 hover:border-gold py-2.5 uppercase text-xs tracking-widest">Sign in</button>
              <button onClick={() => go("/register")} data-testid="drawer-register" className="bg-gold text-black hover:bg-gold-hover py-2.5 uppercase text-xs tracking-widest">Register</button>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
